import React, { useState } from 'react';
import { X, Check, MessageCircle, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { api } from '../lib/api';
import { FormError, FormBanner } from './FormError';
import useFormValidation from '../hooks/useFormValidation';

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

export default function DemoRequestModal({ open, onClose }) {
  const [form, setForm] = useState({
    name: '', company: '', city: '', agents_count: '', properties_count: '',
    whatsapp: '', email: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const v = useFormValidation();

  const set = (k, val) => { setForm((f) => ({ ...f, [k]: val })); v.clearField(k); };

  const submit = async (e) => {
    e?.preventDefault();
    if (submitting) return;
    v.reset();
    const ok = v.validate({
      name: !form.name || form.name.trim().length < 2 ? 'El nombre debe tener al menos 2 caracteres.' : null,
      company: !form.company || form.company.trim().length < 2 ? 'El nombre de la empresa es obligatorio.' : null,
      city: !form.city || form.city.trim().length < 2 ? 'Indica la ciudad principal.' : null,
      whatsapp: !form.whatsapp || form.whatsapp.replace(/\D/g, '').length < 7 ? 'Ingresa un WhatsApp válido.' : null,
      email: !form.email ? 'Email es obligatorio.'
        : !EMAIL_RE.test(form.email) ? 'Email no tiene un formato válido.' : null,
    });
    if (!ok) return;
    setSubmitting(true);
    try {
      await api.createAgencyLead({
        name: form.name.trim(),
        company: form.company.trim(),
        city: form.city.trim(),
        agents_count: form.agents_count ? parseInt(form.agents_count, 10) : null,
        properties_count: form.properties_count ? parseInt(form.properties_count, 10) : null,
        whatsapp: form.whatsapp.trim(),
        email: form.email.trim(),
        notes: form.notes.trim() || null,
      });
      setSent(true);
    } catch (err) {
      v.applyError(err, 'No pudimos enviar tu solicitud. Intenta nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-4" onClick={onClose} data-testid="demo-request-modal">
      <div className="bg-white rounded-3xl max-w-lg w-full max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-gray-100">
          <div>
            <p className="text-xs uppercase tracking-widest text-habita font-bold">Solicitar demostración</p>
            <h2 className="font-serif text-xl font-bold text-habita-dark">Hablemos de tu inmobiliaria</h2>
          </div>
          <button onClick={onClose} aria-label="Cerrar" data-testid="demo-modal-close-btn" className="w-9 h-9 rounded-full hover:bg-gray-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>

        {sent ? (
          <div className="p-10 text-center">
            <div className="w-16 h-16 rounded-full bg-habita-mint mx-auto flex items-center justify-center mb-4"><Check className="w-8 h-8 text-habita" strokeWidth={3} /></div>
            <h3 className="font-serif text-xl font-bold text-habita-dark">¡Solicitud enviada!</h3>
            <p className="text-sm text-gray-600 mt-2">Te contactaremos en menos de <strong>24 horas hábiles</strong> por WhatsApp para agendar tu demo personalizada.</p>
            <p className="text-xs text-gray-500 mt-2">También recibirás un correo de confirmación.</p>
            <Button onClick={onClose} className="btn-habita mt-5 px-8 h-11 rounded-full" data-testid="demo-modal-success-close">Cerrar</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-5 space-y-4" noValidate>
            <FormBanner msg={v.banner} testid="demo-error-banner" />
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Tu nombre</Label>
                <Input value={form.name} onChange={(e) => set('name', e.target.value)} className={`mt-1.5 h-11 ${v.errorClass('name')}`} data-testid="demo-name-input" {...v.field('name')} />
                <FormError msg={v.fieldErrors.name} testid="demo-error-name" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Empresa</Label>
                <Input value={form.company} onChange={(e) => set('company', e.target.value)} className={`mt-1.5 h-11 ${v.errorClass('company')}`} placeholder="Inmobiliaria Norte" data-testid="demo-company-input" {...v.field('company')} />
                <FormError msg={v.fieldErrors.company} testid="demo-error-company" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">Ciudad principal</Label>
                <Input value={form.city} onChange={(e) => set('city', e.target.value)} className={`mt-1.5 h-11 ${v.errorClass('city')}`} placeholder="Bogotá" data-testid="demo-city-input" {...v.field('city')} />
                <FormError msg={v.fieldErrors.city} testid="demo-error-city" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">WhatsApp</Label>
                <Input value={form.whatsapp} onChange={(e) => set('whatsapp', e.target.value)} className={`mt-1.5 h-11 ${v.errorClass('whatsapp')}`} placeholder="+57 300 000 0000" data-testid="demo-whatsapp-input" {...v.field('whatsapp')} />
                <FormError msg={v.fieldErrors.whatsapp} testid="demo-error-whatsapp" />
              </div>
              <div className="sm:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-gray-500">Correo electrónico</Label>
                <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} className={`mt-1.5 h-11 ${v.errorClass('email')}`} placeholder="tu@empresa.com" data-testid="demo-email-input" {...v.field('email')} />
                <FormError msg={v.fieldErrors.email} testid="demo-error-email" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">N° de asesores</Label>
                <Input type="number" inputMode="numeric" value={form.agents_count} onChange={(e) => set('agents_count', e.target.value)} className="mt-1.5 h-11" placeholder="5" data-testid="demo-agents-input" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-gray-500">N° de propiedades</Label>
                <Input type="number" inputMode="numeric" value={form.properties_count} onChange={(e) => set('properties_count', e.target.value)} className="mt-1.5 h-11" placeholder="80" data-testid="demo-properties-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Cuéntanos (opcional)</Label>
              <textarea value={form.notes} onChange={(e) => set('notes', e.target.value)} rows={3} className="mt-1.5 w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-habita" placeholder="¿Qué te interesa de Habita? ¿Tienes CRM actualmente?" data-testid="demo-notes-input" />
            </div>
            <Button type="submit" disabled={submitting} className="btn-habita w-full h-12 rounded-full text-base font-semibold" data-testid="demo-submit-btn">
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enviando...</> : <><MessageCircle className="w-4 h-4 mr-2" /> Enviar solicitud</>}
            </Button>
            <p className="text-[11px] text-gray-500 text-center">Al enviar, aceptas que te contactemos por WhatsApp y email.</p>
          </form>
        )}
      </div>
    </div>
  );
}
